import type {
  BuildSessionReportInput,
  DebugAuditCheckpoint,
  SessionReportAudioCard,
  SessionReportBufferingCard,
  SessionReportConnectionCard,
  SessionReportDashboard,
  SessionReportHealthGroup,
  SessionReportNetworkCard,
  SessionReportRecoveryCard,
  SessionReportResilienceStatus,
  SessionReportSummary,
  SessionReportSummaryOutcome,
  SessionReportSupportFlags,
  SessionReportTranscriptCard,
} from "./types";

export const SESSION_REPORT_THRESHOLDS = {
  partialLatencyMs: 1500,
  finalLatencyMs: 4000,
  recoveryDurationMs: 5000,
  peakBufferedAudioBytes: 262144,
  offlineFlaps: 2,
} as const;

export interface SessionReportResolvedStatus extends SessionReportResilienceStatus {
  timestamp: number;
}

const metric = (
  label: string,
  value: string | number | boolean | null,
  unit?: string | null,
  detail?: string | null
): string | number | boolean | null => value;

const metricRecord = (
  entries: Array<[string, string | number | boolean | null]>
): Record<string, string | number | boolean | null> =>
  entries.reduce<Record<string, string | number | boolean | null>>((record, [key, value]) => {
    record[key] = value;
    return record;
  }, {});

const sortByTimestamp = <T extends { timestamp: number }>(values: T[]): T[] =>
  values.slice().sort((left, right) => left.timestamp - right.timestamp);

const maxNumber = (values: Array<number | null | undefined>): number | null => {
  const filtered = values.filter((value): value is number => typeof value === "number");
  if (filtered.length === 0) {
    return null;
  }
  return Math.max(...filtered);
};

const countPhases = (input: BuildSessionReportInput, type: string): number =>
  input.auditState.phases.filter((phase) => phase.type === type).length;

const countIncidents = (input: BuildSessionReportInput, type: string): number =>
  input.auditState.incidents.filter((incident) => incident.type === type).length;

const resolveCheckpointMetricsSnapshot = (
  input: BuildSessionReportInput
): BuildSessionReportInput["metricsSnapshot"] => {
  const checkpoints = sortByTimestamp(input.auditState.checkpoints);

  for (let index = checkpoints.length - 1; index >= 0; index -= 1) {
    if (checkpoints[index].metricsSnapshot) {
      return checkpoints[index].metricsSnapshot;
    }
  }

  return null;
};

export const resolveMetricsSnapshot = (
  input: BuildSessionReportInput
): BuildSessionReportInput["metricsSnapshot"] =>
  input.metricsSnapshot ?? resolveCheckpointMetricsSnapshot(input);

const getAuditCheckpoints = (input: BuildSessionReportInput): DebugAuditCheckpoint[] => {
  const checkpoints = sortByTimestamp(input.auditState.checkpoints);
  const currentStatus = input.resilienceStatus;
  const currentMetrics = input.metricsSnapshot;
  const currentTimestamp =
    (typeof currentStatus?.timestamp === "number" ? currentStatus.timestamp : null) ??
    currentMetrics?.updatedAt ??
    null;

  if (currentTimestamp === null && !currentStatus && !currentMetrics) {
    return checkpoints;
  }

  const nextCheckpoint: DebugAuditCheckpoint = {
    timestamp: currentTimestamp ?? input.now,
    resilienceStatus: currentStatus
      ? ({
          ...currentStatus,
          timestamp: currentTimestamp ?? input.now,
        } as SessionReportResilienceStatus)
      : null,
    metricsSnapshot: currentMetrics ?? null,
  };

  if (checkpoints[checkpoints.length - 1]?.timestamp === nextCheckpoint.timestamp) {
    checkpoints[checkpoints.length - 1] = nextCheckpoint;
    return checkpoints;
  }

  checkpoints.push(nextCheckpoint);
  return sortByTimestamp(checkpoints);
};

export const resolveSessionReportSupportFlags = (
  metricsSnapshot: BuildSessionReportInput["metricsSnapshot"]
): SessionReportSupportFlags => ({
  transcriptUi: metricsSnapshot?.transcriptUi.supported ?? true,
  session: metricsSnapshot?.session.supported ?? true,
  connection: metricsSnapshot?.connection.supported ?? true,
  recovery: metricsSnapshot?.recovery.supported ?? true,
  buffering: metricsSnapshot?.buffering.supported ?? true,
  browserNetwork: metricsSnapshot?.browserNetwork.supported ?? true,
  audioCapture: metricsSnapshot?.audioCapture.supported ?? false,
});

export const resolveLatestReportStatus = (
  input: BuildSessionReportInput
): SessionReportResolvedStatus | null => {
  const checkpoints = getAuditCheckpoints(input);

  for (let index = checkpoints.length - 1; index >= 0; index -= 1) {
    const status = checkpoints[index].resilienceStatus;

    if (!status) {
      continue;
    }

    return {
      ...status,
      timestamp:
        typeof status.timestamp === "number" ? status.timestamp : checkpoints[index].timestamp,
    };
  }

  return null;
};

const getMaxBufferedAudioBytes = (input: BuildSessionReportInput): number | null => {
  const metricsSnapshot = resolveMetricsSnapshot(input);
  const metricValue =
    metricsSnapshot?.buffering.supported === true
      ? metricsSnapshot.buffering.peakBufferedAudioBytes
      : null;
  const checkpoints = getAuditCheckpoints(input);
  const checkpointValue = maxNumber(
    checkpoints.map((checkpoint) => {
      const metricsValue = checkpoint.metricsSnapshot?.buffering.peakBufferedAudioBytes ?? null;
      const statusValue = checkpoint.resilienceStatus?.totalBufferedAudioBytes ?? null;
      return maxNumber([metricsValue, statusValue]);
    })
  );

  if (typeof metricValue === "number") {
    return maxNumber([metricValue, checkpointValue]);
  }

  return checkpointValue;
};

const getOfflineDurationMs = (input: BuildSessionReportInput): number | null => {
  const metricsSnapshot = resolveMetricsSnapshot(input);
  const metricValue =
    metricsSnapshot?.browserNetwork.supported === true
      ? metricsSnapshot.browserNetwork.offlineDurationMs
      : null;

  if (typeof metricValue === "number") {
    return metricValue;
  }

  const incidents = sortByTimestamp(
    input.auditState.incidents.filter(
      (incident) => incident.type === "offline" || incident.type === "online"
    )
  );
  const latestStatus = resolveLatestReportStatus(input);

  if (incidents.length === 0) {
    return latestStatus?.browserOnline === false ? 0 : null;
  }

  let offlineSince: number | null = null;
  let total = 0;

  for (let index = 0; index < incidents.length; index += 1) {
    const incident = incidents[index];

    if (incident.type === "offline") {
      offlineSince = incident.timestamp;
      continue;
    }

    if (incident.type === "online" && offlineSince !== null) {
      total += Math.max(incident.timestamp - offlineSince, 0);
      offlineSince = null;
    }
  }

  if (offlineSince !== null && latestStatus?.browserOnline === false) {
    total += Math.max(input.now - offlineSince, 0);
  }

  return total;
};

const buildOutcome = (
  input: BuildSessionReportInput,
  summary: Omit<SessionReportSummary, "outcome">
): SessionReportSummaryOutcome => {
  const latestStatus = resolveLatestReportStatus(input);
  const metricsSnapshot = resolveMetricsSnapshot(input);
  const fatalErrorCount =
    metricsSnapshot?.session.supported === true
      ? metricsSnapshot.session.fatalErrorCount
      : null;
  const terminalDisconnectObserved =
    (summary.terminalDisconnectCount ?? 0) > 0 ||
    countIncidents(input, "terminal_disconnect") > 0 ||
    latestStatus?.connectionState === "disconnected";
  const bufferedCheckpointObserved = getAuditCheckpoints(input).some(
    (checkpoint) => Number(checkpoint.resilienceStatus?.totalBufferedAudioBytes ?? 0) > 0
  );
  const bufferedAudioObserved =
    (summary.peakBufferedAudioBytes ?? 0) > 0 ||
    countIncidents(input, "buffering_started") > 0 ||
    bufferedCheckpointObserved;
  const drainStalled =
    latestStatus?.drainExitReason === "stalled" ||
    latestStatus?.finishDeliveryState === "timed_out" ||
    countIncidents(input, "drain_stalled") > 0;
  const bufferedAudioRemaining =
    bufferedAudioObserved &&
    input.auditState.stoppedAt !== null &&
    Number(latestStatus?.totalBufferedAudioBytes ?? 0) > 0;

  if (
    (fatalErrorCount ?? 0) > 0 ||
    (terminalDisconnectObserved && summary.finalTranscriptCount === 0)
  ) {
    return "failed";
  }

  if (drainStalled || bufferedAudioRemaining || terminalDisconnectObserved) {
    return "degraded_unrecovered";
  }

  if (
    (summary.reconnectCount ?? 0) > 0 ||
    (summary.offlineDurationMs ?? 0) > 0 ||
    bufferedAudioObserved
  ) {
    return "degraded_recovered";
  }

  return "healthy";
};

export const deriveSessionReportSummary = (
  input: BuildSessionReportInput
): SessionReportSummary => {
  const metrics = resolveMetricsSnapshot(input);
  const support = resolveSessionReportSupportFlags(metrics);
  const startedAt = input.auditState.startedAt;
  const stoppedAt = input.auditState.stoppedAt;
  const sessionDurationMs =
    startedAt === null ? null : Math.max((stoppedAt ?? input.now) - startedAt, 0);

  const partialTranscriptCount =
    metrics?.transcriptUi.supported === true
      ? metrics.transcriptUi.partialCount ?? null
      : null;
  const finalTranscriptCount =
    metrics?.transcriptUi.supported === true
      ? metrics.transcriptUi.finalCount ?? input.auditState.finalDigests.length
      : input.auditState.finalDigests.length;
  const diarizationCount =
    metrics?.transcriptUi.supported === true
      ? metrics.transcriptUi.diarizationCount ?? null
      : input.auditState.transcriptMilestones.some(
          (milestone) => milestone.type === "first_diarization"
        )
        ? 1
        : 0;

  const partialSummary = {
    sessionDurationMs,
    partialTranscriptCount:
      partialTranscriptCount ??
      (input.auditState.transcriptMilestones.some(
        (milestone) => milestone.type === "first_partial"
      )
        ? 1
        : 0),
    finalTranscriptCount,
    diarizationCount,
    avgFirstPartialLatencyMs:
      support.transcriptUi ? metrics?.transcriptUi.avgFirstPartialLatencyMs ?? null : null,
    p95FirstPartialLatencyMs:
      support.transcriptUi ? metrics?.transcriptUi.p95FirstPartialLatencyMs ?? null : null,
    avgFinalLatencyMs:
      support.transcriptUi ? metrics?.transcriptUi.avgFinalLatencyMs ?? null : null,
    p95FinalLatencyMs:
      support.transcriptUi ? metrics?.transcriptUi.p95FinalLatencyMs ?? null : null,
    avgStabilizationLatencyMs:
      support.transcriptUi
        ? metrics?.transcriptUi.avgStabilizationLatencyMs ?? null
        : null,
    p95StabilizationLatencyMs:
      support.transcriptUi
        ? metrics?.transcriptUi.p95StabilizationLatencyMs ?? null
        : null,
    reconnectCount:
      metrics?.recovery.supported === true
        ? metrics.recovery.reconnectCount
        : countPhases(input, "reconnecting"),
    recoverySuccessCount:
      metrics?.recovery.supported === true
        ? metrics.recovery.recoverySuccessCount
        : countPhases(input, "reconnected"),
    terminalDisconnectCount:
      metrics?.recovery.supported === true
        ? metrics.recovery.terminalDisconnectCount
        : countIncidents(input, "terminal_disconnect"),
    avgRecoveryDurationMs:
      support.recovery ? metrics?.recovery.avgRecoveryDurationMs ?? null : null,
    p95RecoveryDurationMs:
      support.recovery ? metrics?.recovery.p95RecoveryDurationMs ?? null : null,
    peakBufferedAudioBytes: getMaxBufferedAudioBytes(input),
    bufferingDurationMs:
      support.buffering ? metrics?.buffering.bufferingDurationMs ?? null : null,
    drainingDurationMs:
      support.buffering ? metrics?.buffering.drainingDurationMs ?? null : null,
    bufferStallCount:
      support.buffering
        ? metrics?.buffering.bufferStallCount ?? countIncidents(input, "drain_stalled")
        : null,
    drainExitReason:
      support.buffering ? metrics?.buffering.drainExitReason ?? null : null,
    finishDeliveryState:
      support.buffering ? metrics?.buffering.finishDeliveryState ?? null : null,
    offlineDurationMs: getOfflineDurationMs(input),
    flapCount:
      support.browserNetwork ? metrics?.browserNetwork.flapCount ?? null : null,
    rttMs: support.browserNetwork ? metrics?.browserNetwork.rttMs ?? null : null,
    downlinkMbps:
      support.browserNetwork ? metrics?.browserNetwork.downlinkMbps ?? null : null,
    clippingEventCount:
      support.audioCapture ? metrics?.audioCapture.clippingEventCount ?? null : null,
    silenceRatio:
      support.audioCapture ? metrics?.audioCapture.silenceRatio ?? null : null,
    speechActivityRatio:
      support.audioCapture ? metrics?.audioCapture.speechActivityRatio ?? null : null,
    audioCallbackGapCount:
      support.audioCapture ? metrics?.audioCapture.audioCallbackGapCount ?? null : null,
    longestAudioCallbackGapMs:
      support.audioCapture
        ? metrics?.audioCapture.longestAudioCallbackGapMs ?? null
        : null,
  };

  return {
    ...partialSummary,
    outcome: buildOutcome(input, partialSummary),
  };
};

export const buildSummaryMetrics = (
  summary: SessionReportSummary
): SessionReportDashboard["heroStats"] => ({
  outcome: summary.outcome,
  sessionDurationMs: summary.sessionDurationMs,
  finalTranscriptCount: summary.finalTranscriptCount ?? 0,
  reconnectCount: summary.reconnectCount,
  peakBufferedAudioBytes: summary.peakBufferedAudioBytes,
  offlineDurationMs: summary.offlineDurationMs,
});

export const buildTranscriptCard = (
  health: SessionReportHealthGroup,
  summary: SessionReportSummary
): SessionReportTranscriptCard => ({
  status: health.status,
  headline:
    health.status === "critical"
      ? "Transcript needs attention"
      : health.status === "degraded"
        ? "Transcript is degraded"
        : summary.partialTranscriptCount === 0 && summary.finalTranscriptCount === 0
          ? "No transcript activity"
          : "Transcript looks healthy",
  metrics: metricRecord([
    ["partialTranscriptCount", metric("Partial transcripts", summary.partialTranscriptCount)],
    ["finalTranscriptCount", metric("Final transcripts", summary.finalTranscriptCount)],
    ["diarizationCount", metric("Diarization segments", summary.diarizationCount)],
    [
      "avgFirstPartialLatencyMs",
      metric("Average first partial latency", summary.avgFirstPartialLatencyMs),
    ],
    [
      "p95FirstPartialLatencyMs",
      metric("P95 first partial latency", summary.p95FirstPartialLatencyMs),
    ],
    ["avgFinalLatencyMs", metric("Average final latency", summary.avgFinalLatencyMs)],
    ["p95FinalLatencyMs", metric("P95 final latency", summary.p95FinalLatencyMs)],
    [
      "avgStabilizationLatencyMs",
      metric("Average stabilization latency", summary.avgStabilizationLatencyMs),
    ],
    [
      "p95StabilizationLatencyMs",
      metric("P95 stabilization latency", summary.p95StabilizationLatencyMs),
    ],
  ]),
});

export const buildConnectionCard = (
  health: SessionReportHealthGroup,
  latestStatus: SessionReportResolvedStatus | null
): SessionReportConnectionCard => ({
  status: health.status,
  headline:
    health.status === "critical"
      ? "Connection broken"
      : health.status === "degraded"
        ? "Connection recovering"
        : latestStatus?.connectionState === "connected"
          ? "Connection is active"
          : "Connection is idle",
  metrics: metricRecord([
    ["connectionState", metric("Connection state", latestStatus?.connectionState ?? null)],
    ["websocketState", metric("WebSocket state", latestStatus?.websocketState ?? null)],
    ["browserOnline", metric("Browser online", latestStatus?.browserOnline ?? null)],
    ["reconnectAttempt", metric("Reconnect attempt", latestStatus?.reconnectAttempt ?? null)],
    [
      "transportBufferedAmountBytes",
      metric("Transport buffered amount", latestStatus?.transportBufferedAmountBytes ?? null),
    ],
    [
      "lastDisconnectCode",
      metric("Last disconnect code", latestStatus?.lastDisconnect?.code ?? null),
    ],
    [
      "lastDisconnectReason",
      metric("Last disconnect reason", latestStatus?.lastDisconnect?.reason ?? null),
    ],
  ]),
});

export const buildRecoveryCard = (
  health: SessionReportHealthGroup,
  summary: SessionReportSummary,
  latestStatus: SessionReportResolvedStatus | null
): SessionReportRecoveryCard => ({
  status: health.status,
  headline:
    health.status === "critical"
      ? "Recovery failed"
      : (summary.reconnectCount ?? 0) > 0
        ? "Recovery was required"
        : "No recovery needed",
  metrics: metricRecord([
    ["reconnectCount", metric("Reconnect count", summary.reconnectCount)],
    ["recoverySuccessCount", metric("Recovery success count", summary.recoverySuccessCount)],
    [
      "terminalDisconnectCount",
      metric("Terminal disconnect count", summary.terminalDisconnectCount),
    ],
    [
      "currentReconnectAttempt",
      metric("Current reconnect attempt", latestStatus?.reconnectAttempt ?? null),
    ],
    [
      "nextReconnectDelayMs",
      metric("Next reconnect delay", latestStatus?.nextReconnectDelayMs ?? null),
    ],
    [
      "remainingReconnectAttempts",
      metric(
        "Remaining reconnect attempts",
        latestStatus?.remainingReconnectAttempts ?? null
      ),
    ],
    ["avgRecoveryDurationMs", metric("Average recovery duration", summary.avgRecoveryDurationMs)],
    ["p95RecoveryDurationMs", metric("P95 recovery duration", summary.p95RecoveryDurationMs)],
  ]),
});

export const buildBufferingCard = (
  health: SessionReportHealthGroup,
  summary: SessionReportSummary,
  latestStatus: SessionReportResolvedStatus | null
): SessionReportBufferingCard => ({
  status: health.status,
  headline:
    health.status === "critical"
      ? "Buffering stalled"
      : (summary.peakBufferedAudioBytes ?? 0) > 0
        ? "Buffering was observed"
        : "No buffering observed",
  metrics: {
    isBufferingAudio: latestStatus?.isBufferingAudio ?? null,
    isDrainingAudio: latestStatus?.isDrainingAudio ?? null,
    pendingBufferedAudioBytes: latestStatus?.pendingBufferedAudioBytes ?? null,
    persistedBufferedAudioBytes: latestStatus?.persistedBufferedAudioBytes ?? null,
    totalBufferedAudioBytes: latestStatus?.totalBufferedAudioBytes ?? null,
    peakBufferedAudioBytes: summary.peakBufferedAudioBytes,
    bufferingDurationMs: summary.bufferingDurationMs,
    drainingDurationMs: summary.drainingDurationMs,
    bufferStallCount: summary.bufferStallCount,
    drainExitReason: summary.drainExitReason,
    finishDeliveryState: summary.finishDeliveryState,
  },
});

export const buildNetworkCard = (
  health: SessionReportHealthGroup,
  summary: SessionReportSummary,
  input: BuildSessionReportInput
): SessionReportNetworkCard => {
  const latestStatus = resolveLatestReportStatus(input);
  const network = resolveMetricsSnapshot(input)?.browserNetwork ?? null;

  return {
    status: health.status,
    headline:
      health.status === "degraded"
        ? "Browser network was unstable"
        : (summary.offlineDurationMs ?? 0) > 0
          ? "Browser was offline"
          : "Browser network was stable",
    metrics: {
      browserOnline: latestStatus?.browserOnline ?? null,
      offlineDurationMs: summary.offlineDurationMs,
      flapCount: summary.flapCount,
      effectiveType: network?.effectiveType ?? null,
      downlinkMbps: network?.downlinkMbps ?? null,
      rttMs: network?.rttMs ?? null,
    },
  };
};

export const buildAudioCard = (
  health: SessionReportHealthGroup,
  input: BuildSessionReportInput["metricsSnapshot"]
): SessionReportAudioCard => {
  const audio = input?.audioCapture ?? null;

  return {
    status: health.status,
    headline:
      health.status === "degraded"
        ? "Audio capture issues observed"
        : "Audio capture looks healthy",
    metrics: {
      inputSampleRate: audio?.inputSampleRate ?? null,
      targetSampleRate: audio?.targetSampleRate ?? null,
      channelCount: audio?.channelCount ?? null,
      clippingEventCount: audio?.clippingEventCount ?? null,
      silenceRatio: audio?.silenceRatio ?? null,
      speechActivityRatio: audio?.speechActivityRatio ?? null,
      audioCallbackGapCount: audio?.audioCallbackGapCount ?? null,
      longestAudioCallbackGapMs: audio?.longestAudioCallbackGapMs ?? null,
    },
  };
};
